import React, { useState, useEffect } from 'react';
import { createStaff, fetchRolesAPI, inviteStaff } from '../utils/api';
import { mapErrorMessage } from '../utils/errorMapping';
import { ADMIN_ROLE, FINANCIAL_STAFF, FINANCIAL_VIEWER_STAFF } from '../utils/roles';
import './_Buttons.css';
import './_Checkbox.css';
import './_Roles.css';

const CreateStaffModal = ({ onClose, onStaffCreated }) => {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [roles, setRoles] = useState([]);
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [error, setError] = useState('');
    const [inviteMode, setInviteMode] = useState(false);
    useEffect(() => {
        const loadRoles = async () => {
            try {
                const rolesData = await fetchRolesAPI();
                setRoles(rolesData.data);
            } catch (error) {
                setError(mapErrorMessage(error.message));
            }
        };

        loadRoles();
    }, []);

    const handleRoleChange = (e) => {
        const roleId = parseInt(e.target.value, 10);
        const isChecked = e.target.checked;

        if (roleId === ADMIN_ROLE) {
            if (isChecked) {
                setSelectedRoles([ADMIN_ROLE]);
            } else {
                setSelectedRoles([]);
            }
        } else {
            let newRoles = selectedRoles.filter(r => r !== ADMIN_ROLE);
            if (isChecked) {
                newRoles = [...newRoles, roleId];
            } else {
                newRoles = newRoles.filter(id => id !== roleId);
            }
            setSelectedRoles(newRoles);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        let finalRoles = [...selectedRoles];

        if (finalRoles.includes(FINANCIAL_VIEWER_STAFF) && finalRoles.length === 1) {
            setError('Financial Viewer cannot be the only role.');
            return;
        }

        if (inviteMode) {
            if (!email || finalRoles.length === 0) {
                setError('Please fill in email and select at least one role to send an invitation.');
                return;
            }
            try {
                await inviteStaff(email, finalRoles);
                onStaffCreated();
                onClose();
            } catch (error) {
                setError(mapErrorMessage(error.message));
            }
        } else {
            if (!firstName || !lastName || !email || finalRoles.length === 0) {
                setError('Please fill in all fields and select at least one role.');
                return;
            }
            try {
                await createStaff({
                    first_name: firstName,
                    last_name: lastName,
                    email: email,
                    roles: finalRoles
                });
                onStaffCreated();
                onClose();
            } catch (error) {
                setError(mapErrorMessage(error.message));
            }
        }
    };

    const isAdminSelected = selectedRoles.includes(ADMIN_ROLE);
    const isFinancialSelected = selectedRoles.includes(FINANCIAL_STAFF);
    const isFinancialViewerSelected = selectedRoles.includes(FINANCIAL_VIEWER_STAFF);
    const otherRolesSelected = selectedRoles.some(r => r !== ADMIN_ROLE);

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="modal-close" onClick={onClose}>X</button>
                <h2>{inviteMode ? 'Invite New Staff Member' : 'Create New Staff Member'}</h2>
                <div className="actions-centered" style={{ marginBottom: '20px' }}>
                    <button onClick={() => setInviteMode(false)} className={`toggle-button create-toggle ${!inviteMode ? 'active' : ''}`}>Create</button>
                    <button onClick={() => setInviteMode(true)} className={`toggle-button invite-toggle ${inviteMode ? 'active' : ''}`}>Invite</button>
                </div>
                {error && <p className="error">{error}</p>}
                <form onSubmit={handleSubmit}>
                    {!inviteMode && (
                        <>
                            <div className="form-group">
                                <input
                                    id="firstName"
                                    type="text"
                                    placeholder="First Name"
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                />
                            </div>
                            <div className="form-group">
                                <input
                                    id="lastName"
                                    type="text"
                                    placeholder="Last Name"
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                />
                            </div>
                        </>
                    )}
                    <div className="form-group">
                        <input
                            id="email"
                            type="email"
                            placeholder="Email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <p>Roles</p>
                        <div className="checkbox-group">
                            {roles.filter(r => r.id === ADMIN_ROLE).map(role => (
                                <div key={role.id} className="checkbox-item">
                                    <input
                                        type="checkbox"
                                        id={`role-${role.id}`}
                                        value={role.id}
                                        checked={selectedRoles.includes(role.id)}
                                        onChange={handleRoleChange}
                                        disabled={otherRolesSelected}
                                    />
                                    <label htmlFor={`role-${role.id}`}>{role.name}</label>
                                </div>
                            ))}
                            {roles.filter(r => r.id !== ADMIN_ROLE).map(role => (
                                <div key={role.id} className={`checkbox-item ${role.id === FINANCIAL_VIEWER_STAFF && isFinancialSelected ? 'dimmed' : ''}`}>
                                    <input
                                        type="checkbox"
                                        id={`role-${role.id}`}
                                        value={role.id}
                                        checked={selectedRoles.includes(role.id)}
                                        onChange={handleRoleChange}
                                        disabled={isAdminSelected || (role.id === FINANCIAL_VIEWER_STAFF && isFinancialSelected) || (role.id === FINANCIAL_STAFF && isFinancialViewerSelected)}
                                    />
                                    <label htmlFor={`role-${role.id}`}>{role.name}</label>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className='actions-centered'>
                        <button onClick={onClose} className='cancel-button text-button'>Cancel</button>
                        <button type="submit" className={`text-button ${inviteMode ? 'invite-button' : 'create-button'}`}>{inviteMode ? 'Invite' : 'Create'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateStaffModal;